/**
 * @namespace IbdEvents
 * @description A collection of IBD-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new CylindoModelSelectedEvent(model));
 * document.addEventListener(IbdEvents.cylindoModelSelected, (e) => { console.log(e.detail.model) });
 */
export class IbdEvents {
  /** @static @constant {string} Event triggered when the main content is updated */
  static ibdProductUpdated = 'ibd:product:updated';
  /** @static @constant {string} Event triggered when a purchase type is selected */
  static ibdPurchaseTypeSelected = 'ibd:purchase-type:selected';
  /** @static @constant {string} Event triggered when a Cylindo model is selected */
  static ibdCylindoModelSelected = 'ibd:cylindo-model:selected';
  /** @static @constant {string} Event triggered when a Cylindo viewer is updated */
  static ibdCylindoViewerUpdated = 'ibd:cylindo-viewer:updated';
  /** @static @constant {string} Event triggered on first user interaction */
  static ibdFirstInteraction = 'ibd:first-interaction';
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class IbdProductUpdateEvent extends Event {
  /**
   * Creates a new IbdProductUpdateEvent
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the main content
   * @param {string} data.productId - The new product ID
   */
  constructor(data) {
    super(IbdEvents.ibdProductUpdated, { bubbles: true });
    this.detail = {
      data: {
        html: data.html,
        productId: data.productId,
      },
    };
  }
}

/**
 * Event fired when a purchase type is selected
 * @extends {Event}
 */
export class IbdPurchaseTypeSelectedEvent extends Event {
  /**
   * Creates a new PurchaseTypeSelectedEvent
   * @param {string} purchaseType - The new purchase type
   * @param {string} consignmentDate - The new consignment date
   */
  constructor(purchaseType, consignmentDate) {
    super(IbdEvents.ibdPurchaseTypeSelected, { bubbles: true });
    this.detail = {
      purchaseType,
      consignmentDate,
    };
  }
}

/**
 * @typedef {Object} IbdCylindoViewerSettings
 * @property {string} code
 * @property {string} configuration
 * @property {boolean} enable_idle_animation
 * @property {number} idle_animation_delay
 */

/**
 * Event fired when a Cylindo viewer is updated
 * @extends {Event}
 */
export class IbdCylindoViewerUpdatedEvent extends Event {
  /**
   * Creates a new CylindoViewerUpdatedEvent
   * @param {IbdCylindoViewerSettings} settings - The new Cylindo viewer settings
   */
  constructor(settings) {
    super(IbdEvents.ibdCylindoViewerUpdated, { bubbles: true });
    this.detail = {
      settings,
    };
  }
}

/**
 * Event fired when a Cylindo model is selected
 * @extends {Event}
 */
export class IbdCylindoModelSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {string} code - The new Cylindo model code
   */
  constructor(code) {
    super(IbdEvents.ibdCylindoModelSelected, { bubbles: true });
    this.detail = {
      code,
    };
  }
}

/**
 * Event fired on first user interaction
 * @extends {Event}
 */
export class IbdFirstInteractionEvent extends Event {
  /**
   * Creates a new IbdFirstInteractionEvent
   * @param {Event} originalEvent - The original interaction event that triggered this
   * @param {string} interactionType - The type of interaction (click, scroll, keydown, touchstart, mousemove)
   */
  constructor(originalEvent, interactionType) {
    super(IbdEvents.ibdFirstInteraction, { bubbles: true });
    this.detail = {
      originalEvent,
      interactionType,
    };
  }
}
